import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Popover,
  Radio,
  Checkbox,
  Collapse,
  useTheme,
  CircularProgress,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { fetchJurisdictionTree } from '../../redux/actions/userAction';
import FlagEmoji from './FlagEmoji';
import { useSearchTokens } from '../../screens/SearchScreen/searchDesignTokens';

/**
 * Unified country + jurisdiction picker for the contract generation form.
 *
 * Visually matches the chat/search `CompactCountryLanguageSelector`: a single
 * trigger pill that opens a popover containing the country list, with the
 * jurisdiction tree of the selected country expanded inline beneath it.
 *
 * Differences from the chat/search version:
 *   - Country is single-select (a contract is governed by one legal system).
 *   - Selection is fully controlled via props — never written to Redux —
 *     because contract jurisdiction is per-document, not a user preference.
 *   - Reuses `state.user.jurisdictionTrees` as a read-only cache so the same
 *     tree is not refetched.
 */

const COUNTRY_OPTIONS = [
  { value: 'MA', flag: '🇲🇦', labelKey: 'country_maroc' },
  { value: 'AE', flag: '🇦🇪', labelKey: 'country_uae' },
];

const LEVEL_LABEL_KEYS = {
  federal: 'jurisdiction_federal',
  emirate: 'jurisdiction_emirates',
  free_zone: 'jurisdiction_free_zones',
};

const JurisdictionItem = ({ name, count, checked, onChange, disabled, indent = 0, theme }) => (
  <Box
    onClick={disabled ? undefined : onChange}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 0.5,
      py: 0.35,
      px: 1,
      pl: 1 + indent * 1.5,
      cursor: disabled ? 'default' : 'pointer',
      borderRadius: '4px',
      opacity: disabled ? 0.4 : 1,
      transition: 'background-color 0.12s',
      '&:hover': disabled ? {} : { backgroundColor: theme.palette.action.hover },
    }}
  >
    <Checkbox
      checked={checked}
      size="small"
      disabled={disabled}
      sx={{ p: 0.25, '& .MuiSvgIcon-root': { fontSize: '0.95rem' } }}
      disableRipple
    />
    <Typography sx={{
      fontSize: '0.74rem',
      fontWeight: checked ? 600 : 400,
      color: theme.palette.text.primary,
      flexGrow: 1,
    }}>
      {name}
    </Typography>
    {count > 0 && (
      <Typography sx={{ fontSize: '0.62rem', color: theme.palette.text.disabled, fontWeight: 500 }}>
        {count.toLocaleString()}
      </Typography>
    )}
  </Box>
);

const LevelHeader = ({ label, expanded, onToggle, theme }) => (
  <Box
    onClick={onToggle}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 0.4,
      py: 0.3,
      px: 1,
      cursor: 'pointer',
      borderRadius: '4px',
      '&:hover': { backgroundColor: theme.palette.action.hover },
    }}
  >
    {expanded
      ? <ExpandMoreIcon sx={{ fontSize: '0.85rem', color: theme.palette.text.disabled }} />
      : <ChevronRightIcon sx={{ fontSize: '0.85rem', color: theme.palette.text.disabled }} />
    }
    <Typography sx={{
      fontSize: '0.68rem',
      fontWeight: 700,
      color: theme.palette.text.secondary,
      textTransform: 'uppercase',
      letterSpacing: '0.04em',
    }}>
      {label}
    </Typography>
  </Box>
);

const ContractCountryJurisdictionPicker = ({
  country,
  onCountryChange,
  jurisdictionCodes,        // null | string[]   — null/empty = "all"
  onJurisdictionCodesChange,
  disabled = false,
}) => {
  const theme = useTheme();
  const dk = useSearchTokens();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const currentLang = (i18n.language || 'en').split('-')[0];

  const treeData = useSelector(state => state.user?.jurisdictionTrees?.[country]) || null;

  const triggerRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [expandedLevels, setExpandedLevels] = useState({});
  const [expandedFzParents, setExpandedFzParents] = useState({});

  // Lazy-fetch tree for the selected country (only when popover opens).
  useEffect(() => {
    if (anchorEl && country && !treeData) {
      dispatch(fetchJurisdictionTree(country));
    }
  }, [anchorEl, country, treeData, dispatch]);

  const getName = useCallback((nameObj) => {
    if (!nameObj) return '';
    if (typeof nameObj === 'string') return nameObj;
    return nameObj[currentLang] || nameObj.en || nameObj.ar || nameObj.fr || Object.values(nameObj)[0] || '';
  }, [currentLang]);

  const isAll = !Array.isArray(jurisdictionCodes) || jurisdictionCodes.length === 0;
  const allCodes = treeData?.all_codes || [];

  const isJurChecked = (code) => !isAll && jurisdictionCodes.includes(code);

  const handleCountryClick = (cc) => {
    if (cc === country) return;
    onCountryChange(cc);
    // Reset jurisdiction selection — codes are country-scoped.
    onJurisdictionCodesChange(null);
  };

  const handleClickAll = () => onJurisdictionCodesChange(null);

  const handleClickJurisdiction = (code) => {
    if (isAll) {
      onJurisdictionCodesChange([code]);
      return;
    }
    if (jurisdictionCodes.includes(code)) {
      const next = jurisdictionCodes.filter(c => c !== code);
      onJurisdictionCodesChange(next.length === 0 ? null : next);
    } else {
      const next = [...jurisdictionCodes, code];
      onJurisdictionCodesChange(next.length >= allCodes.length ? null : next);
    }
  };

  // ─── Display label on the trigger ───────────────────────────────────────

  const getJurisdictionShortName = useCallback((code) => {
    if (!treeData) return code;
    for (const group of treeData.tree) {
      if (group.level === 'free_zone') {
        for (const pg of group.children) {
          for (const z of pg.zones) if (z.code === code) return getName(z.name);
        }
      } else {
        for (const child of group.children) if (child.code === code) return getName(child.name);
      }
    }
    return code;
  }, [treeData, getName]);

  const triggerLabel = (() => {
    const opt = COUNTRY_OPTIONS.find(c => c.value === country);
    const countryName = opt ? t(opt.labelKey, opt.value) : country;
    const flag = opt?.flag;

    let jurPart = '';
    if (treeData?.has_sub_jurisdictions) {
      if (isAll) {
        jurPart = t('jurisdiction_select_all') || 'All jurisdictions';
      } else {
        const names = jurisdictionCodes.map(c => getJurisdictionShortName(c));
        jurPart = names.length <= 2 ? names.join(', ') : `${names[0]} +${names.length - 1}`;
      }
    }

    return { flag, countryName, jurPart };
  })();

  const isOpen = Boolean(anchorEl);

  // ─── Render ─────────────────────────────────────────────────────────────

  return (
    <>
      <Box
        ref={triggerRef}
        onClick={() => !disabled && setAnchorEl(triggerRef.current)}
        sx={{
          // Match the MUI OutlinedInput "small" Select used elsewhere in the
          // contract dialog (selectSx in TemplateOption).
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          height: 40,
          px: '14px',
          borderRadius: '8px',
          cursor: disabled ? 'not-allowed' : 'pointer',
          border: `1px solid ${dk.border}`,
          backgroundColor: dk.inputBg,
          opacity: disabled ? 0.5 : 1,
          transition: 'border-color 0.15s',
          '&:hover': disabled ? {} : { borderColor: dk.borderHover },
        }}
      >
        {triggerLabel.flag && (
          <FlagEmoji emoji={triggerLabel.flag} size={18} />
        )}
        <Typography sx={{
          fontSize: '0.82rem',
          fontWeight: 500,
          color: dk.text1,
          flexGrow: 1,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}>
          {triggerLabel.countryName}
          {triggerLabel.jurPart && (
            <Typography
              component="span"
              sx={{
                fontSize: '0.78rem',
                fontWeight: 400,
                color: dk.text3,
                ml: 0.75,
              }}
            >
              · {triggerLabel.jurPart}
            </Typography>
          )}
        </Typography>
        <ExpandMoreIcon sx={{ fontSize: '1.25rem', color: dk.text3 }} />
      </Box>

      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { mt: 0.5 } } }}
      >
        <Box sx={{ minWidth: 300, maxWidth: 360, maxHeight: 460, overflowY: 'auto', py: 1 }}>
          {COUNTRY_OPTIONS.map(opt => {
            const isSelected = country === opt.value;
            const showTree = isSelected && treeData?.has_sub_jurisdictions;
            return (
              <Box key={opt.value}>
                <Box
                  onClick={() => handleCountryClick(opt.value)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    py: 0.75,
                    px: 1.5,
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: theme.palette.action.hover },
                  }}
                >
                  <Radio
                    checked={isSelected}
                    size="small"
                    sx={{ p: 0.5 }}
                    disableRipple
                  />
                  <Typography
                    component="span"
                    sx={{
                      fontSize: '0.85rem',
                      fontWeight: isSelected ? 600 : 400,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                    }}
                  >
                    <FlagEmoji emoji={opt.flag} size={18} /> {t(opt.labelKey, opt.value)}
                  </Typography>
                </Box>

                {/* Loading state for selected country before tree arrives */}
                {isSelected && !treeData && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
                    <CircularProgress size={16} />
                  </Box>
                )}

                {/* Inline jurisdiction tree under the selected country */}
                {showTree && (
                  <Box sx={{ pl: 1.5, pr: 0.5, pb: 0.5 }}>
                    {/* "All jurisdictions" reset row */}
                    <Box
                      onClick={handleClickAll}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 0.5,
                        py: 0.4, px: 1, mb: 0.3,
                        cursor: 'pointer', borderRadius: '6px',
                        backgroundColor: isAll ? `${theme.palette.primary.main}18` : 'transparent',
                        border: isAll ? `1px solid ${theme.palette.primary.main}40` : '1px solid transparent',
                        '&:hover': {
                          backgroundColor: isAll ? `${theme.palette.primary.main}22` : theme.palette.action.hover,
                        },
                      }}
                    >
                      <Typography sx={{
                        fontSize: '0.74rem',
                        fontWeight: isAll ? 700 : 500,
                        color: isAll ? theme.palette.primary.main : theme.palette.text.secondary,
                      }}>
                        {t('jurisdiction_select_all') || 'All jurisdictions'}
                      </Typography>
                    </Box>

                    {treeData.tree.map((group) => {
                      const levelKey = group.level;
                      const isExpanded = expandedLevels[levelKey] !== false;

                      if (group.level === 'free_zone') {
                        return (
                          <Box key={levelKey}>
                            <LevelHeader
                              label={t(LEVEL_LABEL_KEYS.free_zone) || 'Free zones'}
                              expanded={isExpanded}
                              onToggle={() => setExpandedLevels(p => ({ ...p, [levelKey]: !isExpanded }))}
                              theme={theme}
                            />
                            <Collapse in={isExpanded}>
                              {group.children.map((parentGroup) => {
                                const fzKey = `fz_${parentGroup.parent_code}`;
                                const isFzExpanded = expandedFzParents[fzKey] !== false;
                                return (
                                  <Box key={fzKey}>
                                    <Box
                                      onClick={() => setExpandedFzParents(p => ({ ...p, [fzKey]: !isFzExpanded }))}
                                      sx={{
                                        display: 'flex', alignItems: 'center', gap: 0.3,
                                        py: 0.2, px: 1, pl: 2.5,
                                        cursor: 'pointer', borderRadius: '4px',
                                        '&:hover': { backgroundColor: theme.palette.action.hover },
                                      }}
                                    >
                                      {isFzExpanded
                                        ? <ExpandMoreIcon sx={{ fontSize: '0.75rem', color: theme.palette.text.disabled }} />
                                        : <ChevronRightIcon sx={{ fontSize: '0.75rem', color: theme.palette.text.disabled }} />
                                      }
                                      <Typography sx={{
                                        fontSize: '0.68rem',
                                        fontWeight: 500,
                                        color: theme.palette.text.secondary,
                                        fontStyle: 'italic',
                                      }}>
                                        {getName(parentGroup.parent_name)}
                                      </Typography>
                                    </Box>
                                    <Collapse in={isFzExpanded}>
                                      {parentGroup.zones.map((zone) => (
                                        <JurisdictionItem
                                          key={zone.code}
                                          name={getName(zone.name)}
                                          count={zone.doc_count}
                                          checked={isJurChecked(zone.code)}
                                          onChange={() => handleClickJurisdiction(zone.code)}
                                          disabled={zone.doc_count === 0}
                                          indent={2}
                                          theme={theme}
                                        />
                                      ))}
                                    </Collapse>
                                  </Box>
                                );
                              })}
                            </Collapse>
                          </Box>
                        );
                      }

                      return (
                        <Box key={levelKey}>
                          <LevelHeader
                            label={t(LEVEL_LABEL_KEYS[group.level] || group.level) || group.level}
                            expanded={isExpanded}
                            onToggle={() => setExpandedLevels(p => ({ ...p, [levelKey]: !isExpanded }))}
                            theme={theme}
                          />
                          <Collapse in={isExpanded}>
                            {group.children.map((child) => (
                              <JurisdictionItem
                                key={child.code}
                                name={getName(child.name)}
                                count={child.doc_count}
                                checked={isJurChecked(child.code)}
                                onChange={() => handleClickJurisdiction(child.code)}
                                disabled={child.doc_count === 0}
                                indent={1}
                                theme={theme}
                              />
                            ))}
                          </Collapse>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      </Popover>
    </>
  );
};

export default ContractCountryJurisdictionPicker;
